import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import * as RadixPopover from "@radix-ui/react-popover";
import { Button, Label, ScrollArea, token } from "@may-i/mash-design-system";
import { useQueryClient } from "@tanstack/react-query";
import { PageSpinner } from "~/shared/ui/PageSpinner";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useSelectedVersion } from "~/features/branch-version/hooks/use-selected-version";
import { createCamera } from "~/features/camera/api/create-camera";
import {
  useCreateCamera,
  useDeleteCamera,
  useUpdateCameraImage,
} from "~/features/camera/hooks/use-camera-mutations";
import { CAMERAS_QUERY_KEY, useCameras } from "~/features/camera/hooks/use-cameras";
import { useAssignNNumbers } from "~/features/camera/hooks/use-assign-n-numbers";
import {
  useCreateFloorPlan,
  useDeleteFloorPlan,
  useReorderFloorPlans,
  useUpdateFloorPlan,
} from "~/features/floor-plan/hooks/use-floor-plan-mutations";
import { useFloorPlans } from "~/features/floor-plan/hooks/use-floor-plans";
import type { FloorPlan } from "~/entities/floor-plan/types";
import { isCameraAnalyzed } from "~/entities/camera/utils";
import { generateTempImageFile } from "~/shared/lib/generate-temp-image";
import { useBranches } from "~/features/branch/hooks/use-branches";
import { useCurrentUser } from "~/features/auth/hooks/use-current-user";
import { useUnsavedChangesGuard } from "~/shared/hooks/use-unsaved-changes-guard";
import { CameraCard, NewCameraCard } from "./CameraCard";
import { useMutationErrorToast } from "~/shared/api/use-mutation-error-toast";
import { useToast } from "~/shared/ui/toast";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { FloorPlanCard, NewFloorPlanCard, type DropIndicatorPosition } from "./FloorPlanCard";
import { MultiCameraModal } from "./MultiCameraModal";
import { WhitelistTab } from "./WhitelistTab";

export function ResourceManagementPage() {
  const { t } = useTranslation("resource");
  const { t: tShared } = useTranslation("shared");
  const { showToast } = useToast();
  const showError = useMutationErrorToast();
  const queryClient = useQueryClient();
  const { branchUid } = useParams();
  const { data: currentUser } = useCurrentUser();
  const { selectedVersion } = useSelectedVersion();
  const versionId = selectedVersion?.id;
  const isVersionInactive = selectedVersion ? !selectedVersion.isActive : false;

  if (currentUser && !currentUser.isStaff) {
    return <Navigate to={`/branch/${branchUid}/camera-mapping`} replace />;
  }

  const { data: branches = [] } = useBranches();
  const currentBranch = branches.find((b) => b.uid === branchUid);
  const clientUid = currentBranch?.client.uid;

  const { data: floorPlans = [], isPending: isFloorPlansPending } = useFloorPlans(branchUid, versionId);
  const { data: cameras = [], isPending: isCamerasPending } = useCameras(branchUid, versionId);
  useAssignNNumbers(cameras, branchUid, versionId, {
    onErrors: (failed) => {
      showToast({
        type: "error",
        title: tShared("toast.assignNNumberError.title"),
        content: tShared("toast.assignNNumberError.content", { failed }),
      });
    },
  });

  // 목록 하단으로 스크롤. 새 카드(입력칸/추가 결과)가 맨 아래에 생기므로 화면이 따라가야 한다.
  const scrollTabContentToBottom = useCallback(() => {
    // DS ScrollArea 는 root 에 ref 를 넘기므로 실제 스크롤되는 viewport 를 찾아 이동한다.
    // ⚠️ 이 viewport 에서는 behavior:"smooth" 가 무시되므로(직접 확인) scrollTop 을 바로 설정.
    // ⚠️ 추가된 카드의 이미지가 나중에 로드되며 높이가 커져서, 한 번만 스크롤하면 하단에 못 닿는다
    //    → 짧은 구간 동안 몇 번 다시 맞춘다. 사용자가 직접 스크롤하면 즉시 중단.
    const viewport = tabContentRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;

    let cancelled = false;
    const cancel = () => { cancelled = true; };
    // 사용자가 직접 스크롤을 시작하면 애니메이션을 즉시 양보한다
    viewport.addEventListener("wheel", cancel, { passive: true });
    viewport.addEventListener("touchstart", cancel, { passive: true });

    const DURATION = 420;
    const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
    const from = viewport.scrollTop;
    const start = performance.now();
    const bottom = () => viewport.scrollHeight - viewport.clientHeight;

    let raf = 0;
    let tailUntil = 0;
    const step = (now: number) => {
      if (cancelled) return;
      const elapsed = now - start;
      if (elapsed < DURATION) {
        // 목표는 매 프레임 다시 계산 — 이미지가 뒤늦게 로드되며 높이가 자란다
        viewport.scrollTop = from + (bottom() - from) * easeOutCubic(elapsed / DURATION);
        raf = requestAnimationFrame(step);
        return;
      }
      // 도착 후에도 잠깐(=tail) 늦게 자란 높이만큼 따라붙는다
      if (!tailUntil) tailUntil = now + 1000;
      viewport.scrollTop = bottom();
      if (now < tailUntil) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(raf);
      viewport.removeEventListener("wheel", cancel);
      viewport.removeEventListener("touchstart", cancel);
    };
  }, []);

  // (4-5-3) 빈 번호 채우기 X — 현재 max + 1 부터 뒤로 쌓는다
  const nonAnalysisNumberMap = useMemo(() => {
    const map = new Map<number, string>();
    const usedNNumbers = new Set<string>();
    let maxN = 0;
    for (const cam of cameras) {
      if (cam.analysisNumber != null && cam.analysisNumber.startsWith("N")) {
        map.set(cam.id, cam.analysisNumber);
        usedNNumbers.add(cam.analysisNumber);
        const v = parseInt(cam.analysisNumber.slice(1), 10);
        if (Number.isFinite(v) && v > maxN) maxN = v;
      }
    }
    for (const cam of cameras) {
      if (cam.analysisNumber === null) {
        let n = maxN + 1;
        while (usedNNumbers.has(`N${n}`)) n++;
        const num = `N${n}`;
        map.set(cam.id, num);
        usedNNumbers.add(num);
        maxN = n;
      }
    }
    return map;
  }, [cameras]);

  // 카메라 정렬: 분석 카메라(숫자 오름) → 미분석(N번호 숫자 오름)
  const sortedCameras = useMemo(() => {
    const list = [...cameras];
    list.sort((a, b) => {
      const aNum = a.analysisNumber;
      const bNum = b.analysisNumber;
      const aIsAnalyzed = aNum != null && !aNum.startsWith("N");
      const bIsAnalyzed = bNum != null && !bNum.startsWith("N");
      if (aIsAnalyzed !== bIsAnalyzed) return aIsAnalyzed ? -1 : 1;
      const aDisplay = aNum ?? nonAnalysisNumberMap.get(a.id) ?? "";
      const bDisplay = bNum ?? nonAnalysisNumberMap.get(b.id) ?? "";
      const aN = aDisplay.startsWith("N") ? parseInt(aDisplay.slice(1), 10) : parseInt(aDisplay, 10);
      const bN = bDisplay.startsWith("N") ? parseInt(bDisplay.slice(1), 10) : parseInt(bDisplay, 10);
      if (Number.isFinite(aN) && Number.isFinite(bN)) return aN - bN;
      return aDisplay.localeCompare(bDisplay, undefined, { numeric: true });
    });
    return list;
  }, [cameras, nonAnalysisNumberMap]);

  const undeletableFloorPlanIds = useMemo(() => {
    const ids = new Set<number>();
    for (const cam of cameras) {
      if (cam.floorPlanId !== null && isCameraAnalyzed(cam.analysisNumber)) {
        ids.add(cam.floorPlanId);
      }
    }
    return ids;
  }, [cameras]);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") ?? "floorPlan";
  const [activeTab, setActiveTab] = useState(initialTab);

  const changeTab = useCallback((tab: string) => {
    setActiveTab(tab);
    setSelectingCameras(false);
    setSelectedCameraIds(new Set());
    if (tab === "floorPlan") {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab }, { replace: true });
    }
  }, [setSearchParams]);

  const [addingFloorPlan, setAddingFloorPlan] = useState(false);
  const [addingCamera, setAddingCamera] = useState(false);
  const [cameraMenuOpen, setCameraMenuOpen] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [reorderedFloorPlans, setReorderedFloorPlans] = useState<FloorPlan[]>([]);
  const [dropIndicator, setDropIndicator] = useState<DropIndicatorPosition>(null);
  const cardGridRef = useRef<HTMLDivElement>(null);
  const tabContentRef = useRef<HTMLDivElement>(null);
  const scrollAfterAddRef = useRef(false);
  const [multiCameraOpen, setMultiCameraOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "floorPlan" | "camera";
    id: number;
    name: string;
  } | null>(null);
  const [selectingCameras, setSelectingCameras] = useState(false);
  const [selectedCameraIds, setSelectedCameraIds] = useState<Set<number>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // 추가 결과(단일/일괄 저장 완료) 후 하단으로
  useEffect(() => {
    if (!scrollAfterAddRef.current) return;
    scrollAfterAddRef.current = false;
    return scrollTabContentToBottom();
  }, [cameras, scrollTabContentToBottom]);

  // 입력 카드가 열리는 시점에도 하단으로 — 새 카드는 목록 맨 끝에 생겨서 화면 밖이다
  useEffect(() => {
    if (!addingCamera && !addingFloorPlan) return;
    return scrollTabContentToBottom();
  }, [addingCamera, addingFloorPlan, scrollTabContentToBottom]);

  // 저장하지 않은 변경사항: 신규 추가 폼 입력 중, 도면 순서 변경 모드, 분할 카메라 모달 진행 중
  const hasUnsavedChanges = addingFloorPlan || addingCamera || reordering || multiCameraOpen;
  useUnsavedChangesGuard(hasUnsavedChanges, tShared("unsavedChangesGuard.message"));

  // --- Floor Plan mutations ---
  const createFloorPlanMut = useCreateFloorPlan(branchUid!, versionId);
  const updateFloorPlanMut = useUpdateFloorPlan(branchUid!, versionId);
  const deleteFloorPlanMut = useDeleteFloorPlan(branchUid!, versionId);
  const reorderFloorPlansMut = useReorderFloorPlans(branchUid!, versionId);

  // --- Camera mutations ---
  const createCameraMut = useCreateCamera(branchUid!, versionId);
  const updateCameraImageMut = useUpdateCameraImage(branchUid!, versionId);
  const deleteCameraMut = useDeleteCamera(branchUid!, versionId);

  // --- Floor Plan handlers ---

  const handleFloorPlanSave = useCallback(
    (id: number, data: { name?: string; imageFile?: File }) => {
      updateFloorPlanMut.mutate(
        {
          floorPlanId: id,
          name: data.name,
          image: data.imageFile,
        },
        {
          onError: (e) =>
            showError(e, {
              title: t("toast.updateFloorPlanError.title"),
              content: t("toast.updateFloorPlanError.content"),
            }),
        },
      );
    },
    [updateFloorPlanMut, showError, t],
  );

  const handleFloorPlanDelete = useCallback(
    (id: number, name: string) => {
      setDeleteTarget({ type: "floorPlan", id, name });
    },
    [],
  );

  const handleNewFloorPlanSave = useCallback(
    async (data: { name: string; imageFile?: File }) => {
      try {
        let image = data.imageFile;
        if (!image) {
          image = await generateTempImageFile();
        }
        await createFloorPlanMut.mutateAsync({ name: data.name, image });
        setAddingFloorPlan(false);
      } catch (e) {
        console.error("[handleNewFloorPlanSave] error:", e);
        showError(e, {
          title: t("toast.addFloorPlanError.title"),
          content: t("toast.addFloorPlanError.content"),
        });
      }
    },
    [createFloorPlanMut, showError, t],
  );

  // --- Camera handlers ---

  const handleCameraImageSave = useCallback(
    (id: number, data: { imageFile?: File }) => {
      if (data.imageFile) {
        updateCameraImageMut.mutate(
          {
            cameraOrientationId: id,
            previewImage: data.imageFile,
          },
          {
            onError: (e) =>
              showError(e, {
                title: t("toast.updateCameraImageError.title"),
                content: t("toast.updateCameraImageError.content"),
              }),
          },
        );
      }
    },
    [updateCameraImageMut, showError, t],
  );

  const handleCameraDelete = useCallback(
    (cameraOrientationId: number, name: string) => {
      setDeleteTarget({ type: "camera", id: cameraOrientationId, name });
    },
    [],
  );

  const handleToggleCameraSelect = useCallback((id: number) => {
    setSelectedCameraIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleStartCameraSelect = useCallback(() => {
    setSelectingCameras(true);
    setSelectedCameraIds(new Set());
  }, []);

  const handleCancelCameraSelect = useCallback(() => {
    setSelectingCameras(false);
    setSelectedCameraIds(new Set());
  }, []);

  // 분석 중 카메라는 삭제 불가 (FE 가드). 전체 선택은 미분석만 대상으로
  const deletableCameras = useMemo(
    () => cameras.filter((c) => !isCameraAnalyzed(c.analysisNumber)),
    [cameras],
  );
  const handleSelectAllCameras = useCallback(() => {
    setSelectedCameraIds(new Set(deletableCameras.map((c) => c.id)));
  }, [deletableCameras]);

  const handleDeselectAllCameras = useCallback(() => {
    setSelectedCameraIds(new Set());
  }, []);

  const handleBulkDeleteConfirm = useCallback(async () => {
    const ids = Array.from(selectedCameraIds);
    const results = await Promise.allSettled(
      ids.map((id) => deleteCameraMut.mutateAsync(id)),
    );
    const failedCount = results.filter((r) => r.status === "rejected").length;
    if (failedCount > 0) {
      showToast({ type: "error", title: t("toast.bulkDeletePartial.title"), content: t("toast.bulkDeletePartial.content", { failed: failedCount, total: ids.length }) });
    } else {
      showToast({ type: "success", title: t("toast.bulkDeleteSuccess.title"), content: t("toast.bulkDeleteSuccess.content", { count: ids.length }) });
    }
    setBulkDeleteOpen(false);
    setSelectingCameras(false);
    setSelectedCameraIds(new Set());
  }, [selectedCameraIds, deleteCameraMut, showToast, t]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    const { type, id } = deleteTarget;
    try {
      if (type === "floorPlan") {
        await deleteFloorPlanMut.mutateAsync(id);
      } else {
        await deleteCameraMut.mutateAsync(id);
      }
      setDeleteTarget(null);
    } catch (e) {
      console.error("[handleDeleteConfirm] error:", e);
      showError(e, {
        title: t("toast.deleteError.title"),
        content: t("toast.deleteError.content"),
      });
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteFloorPlanMut, deleteCameraMut, showError, t]);

  // --- Reorder handlers ---

  const handleStartReorder = useCallback(() => {
    setReorderedFloorPlans([...floorPlans]);
    setReordering(true);
  }, [floorPlans]);

  const handleCancelReorder = useCallback(() => {
    setReordering(false);
    setReorderedFloorPlans([]);
  }, []);

  const handleSaveReorder = useCallback(async () => {
    try {
      await reorderFloorPlansMut.mutateAsync(reorderedFloorPlans.map((fp) => fp.id));
      setReordering(false);
      setReorderedFloorPlans([]);
    } catch (e) {
      console.error("[handleSaveReorder] error:", e);
      showError(e, {
        title: t("toast.reorderError.title"),
        content: t("toast.reorderError.content"),
      });
    }
  }, [reorderFloorPlansMut, reorderedFloorPlans, showError, t]);

  // --- 카드의 실시간 edge를 계산하는 헬퍼 ---
  const getEdgeFromMouse = useCallback((clientX: number, targetIndex: number) => {
    if (!cardGridRef.current) return "right";
    const card = cardGridRef.current.querySelector(
      `[data-card-index="${targetIndex}"]`,
    ) as HTMLElement | null;
    if (!card) return "right";
    const rect = card.getBoundingClientRect();
    return clientX < rect.left + rect.width / 2 ? "left" : "right";
  }, []);

  // --- Drag and drop monitor ---
  useEffect(() => {
    if (!reordering) return;

    const cleanup = monitorForElements({
      onDrag: ({ location, source }) => {
        const target = location.current.dropTargets[0];
        if (!target) {
          setDropIndicator(null);
          return;
        }
        const sourceIndex = source.data.index as number;
        const targetIndex = target.data.index as number;
        const clientX = location.current.input.clientX;
        const edge = getEdgeFromMouse(clientX, targetIndex) as "left" | "right";

        // 의미 없는 위치에는 인디케이터를 표시하지 않음
        if (sourceIndex === targetIndex) {
          setDropIndicator(null);
          return;
        }
        // source 바로 옆의 인접 edge는 이동이 없으므로 숨김
        if (sourceIndex === targetIndex - 1 && edge === "left") {
          setDropIndicator(null);
          return;
        }
        if (sourceIndex === targetIndex + 1 && edge === "right") {
          setDropIndicator(null);
          return;
        }

        setDropIndicator({ targetIndex, edge });
      },
      onDrop: ({ location, source }) => {
        setDropIndicator(null);
        const sourceIndex = source.data.index as number;
        const target = location.current.dropTargets[0];
        if (!target) return;
        const targetIndex = target.data.index as number;
        const clientX = location.current.input.clientX;
        const edge = getEdgeFromMouse(clientX, targetIndex);
        if (sourceIndex === targetIndex) return;

        setReorderedFloorPlans((prev) => {
          const next = prev.slice();
          const [removed] = next.splice(sourceIndex, 1);
          const adjustedTarget = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
          const insertIndex = edge === "right" ? adjustedTarget + 1 : adjustedTarget;
          next.splice(insertIndex, 0, removed);
          return next;
        });
      },
      onDragStart: () => {
        setDropIndicator(null);
      },
    });

    return cleanup;
  }, [reordering, getEdgeFromMouse]);

  // N번호는 BE 가 카메라 생성 시점에 부여한다(이전의 FE 2차 PATCH 부여는 새 카메라가 N번호 없이
  // 떴다가 재정렬되며 깜빡이는 문제를 유발해 제거). 레거시(analyzed_id null) 카메라는 아래
  // useAssignNNumbers 워치독이 폴백으로 부여한다.

  const handleNewCameraSave = useCallback(
    async (data: { imageFile?: File }) => {
      if (!data.imageFile) return;
      try {
        // N번호는 BE 가 생성 시 부여 (FE 2차 PATCH 제거 → 깜빡임 해소)
        await createCameraMut.mutateAsync({ previewImage: data.imageFile });
        // 단일 추가도 일괄 추가와 동일하게 목록 하단으로 자동 스크롤 (추가 결과가 화면 밖이라 확인 불가했음)
        scrollAfterAddRef.current = true;
        setAddingCamera(false);
      } catch (e) {
        console.error("[handleNewCameraSave] error:", e);
        showError(e, {
          title: t("toast.addCameraError.title"),
          content: t("toast.addCameraError.content"),
        });
      }
    },
    [createCameraMut, showError, t],
  );

  const handleMultiCameraComplete = useCallback(
    async (blobs: Blob[]) => {
      if (!branchUid) return;
      const results = await Promise.allSettled(
        blobs.map((blob, i) => {
          const file = new File([blob], `camera-${Date.now()}-${i}.png`, { type: "image/png" });
          return createCamera({ branchUid, versionId, previewImage: file });
        }),
      );
      const failedCount = results.filter((r) => r.status === "rejected").length;
      // (RL6) 하나라도 성공했을 때만 스크롤 예약 — 전부 실패면 목록 변화 없어 플래그가 남아 다음 변경에 엉뚱 스크롤
      if (failedCount < blobs.length) scrollAfterAddRef.current = true;
      // 일괄 완료 후 한 번만 캐시 갱신
      await queryClient.invalidateQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
      if (failedCount === blobs.length) {
        showToast({
          type: "error",
          title: t("toast.addMultiCameraError.title"),
          content: t("toast.addMultiCameraError.content", { total: blobs.length }),
        });
      } else if (failedCount > 0) {
        showToast({
          type: "error",
          title: t("toast.addMultiCameraPartial.title"),
          content: t("toast.addMultiCameraPartial.content", { failed: failedCount, total: blobs.length }),
        });
      }
    },
    [branchUid, versionId, queryClient, showToast, t],
  );

  if (isFloorPlansPending || isCamerasPending) {
    return <Container><PageSpinner /></Container>;
  }

  return (
    <Container>
      <TitleRow>
        <Title>{t("title")}</Title>
      </TitleRow>
      <TabSection>
        <TabRow>
          <TabList>
            <TabTrigger
              $active={activeTab === "floorPlan"}
              disabled={reordering}
              onClick={() => !reordering && changeTab("floorPlan")}
            >
              {t("tab.floorPlan")}
              <Label variant="filled" color="default" size="small">
                {floorPlans.length}
              </Label>
            </TabTrigger>
            <TabTrigger
              $active={activeTab === "camera"}
              disabled={reordering}
              onClick={() => !reordering && changeTab("camera")}
            >
              {t("tab.camera")}
              <Label variant="filled" color="default" size="small">
                {cameras.length}
              </Label>
            </TabTrigger>
            <TabTrigger
              $active={activeTab === "whitelist"}
              disabled={reordering}
              onClick={() => !reordering && changeTab("whitelist")}
            >
              {t("tab.whitelist")}
            </TabTrigger>
          </TabList>
          <ButtonGroup>
            {activeTab === "whitelist" ? null : reordering ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleCancelReorder}
                >
                  {t("action.cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={reorderFloorPlansMut.isPending}
                  onClick={handleSaveReorder}
                >
                  {t("action.saveOrder")}
                </Button>
              </>
            ) : (
              <>
                {activeTab === "floorPlan" && floorPlans.length >= 2 && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleStartReorder}
                  >
                    {t("action.reorder")}
                  </Button>
                )}
                {activeTab === "floorPlan" ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={!versionId}
                    onClick={() => setAddingFloorPlan(true)}
                  >
                    {t("action.addFloorPlan")}
                  </Button>
                ) : selectingCameras ? (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={handleCancelCameraSelect}
                    >
                      {t("action.cancel")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={
                        selectedCameraIds.size === deletableCameras.length && deletableCameras.length > 0
                          ? handleDeselectAllCameras
                          : handleSelectAllCameras
                      }
                    >
                      {selectedCameraIds.size === deletableCameras.length && deletableCameras.length > 0
                        ? t("action.deselectAll")
                        : t("action.selectAll")}
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      disabled={selectedCameraIds.size === 0}
                      onClick={() => setBulkDeleteOpen(true)}
                    >
                      {t("action.deleteSelected", { count: selectedCameraIds.size })}
                    </Button>
                  </>
                ) : (
                  <>
                    {cameras.length > 0 && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleStartCameraSelect}
                      >
                        {t("action.bulkDelete")}
                      </Button>
                    )}
                    <RadixPopover.Root open={cameraMenuOpen} onOpenChange={setCameraMenuOpen}>
                      <RadixPopover.Trigger asChild>
                        <div>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={!versionId}
                          >
                            {t("action.addCamera")}
                          </Button>
                        </div>
                      </RadixPopover.Trigger>
                      <RadixPopover.Portal>
                        <CameraMenuContent sideOffset={4} align="end">
                          <CameraMenuItem
                            onClick={() => {
                              setCameraMenuOpen(false);
                              setAddingCamera(true);
                            }}
                          >
                            {t("action.addSingleCamera")}
                          </CameraMenuItem>
                          <CameraMenuItem
                            onClick={() => {
                              setCameraMenuOpen(false);
                              setMultiCameraOpen(true);
                            }}
                          >
                            {t("action.addSplitCamera")}
                          </CameraMenuItem>
                        </CameraMenuContent>
                      </RadixPopover.Portal>
                    </RadixPopover.Root>
                  </>
                )}
              </>
            )}
          </ButtonGroup>
        </TabRow>
        {/* ref 는 DS ScrollArea 가 아니라 바깥 div 에 — ScrollArea 의 ref 는 실제 스크롤 viewport 를
            품고 있지 않아서 viewport 를 찾지 못한다 (직접 확인) */}
        <TabContent ref={tabContentRef}>
          <ScrollArea>
            <TabContentInner>
          {activeTab === "floorPlan" && (
            <>
              {(() => {
                const displayFloorPlans = reordering ? reorderedFloorPlans : floorPlans;
                if (displayFloorPlans.length > 0 || addingFloorPlan) {
                  return (
                    <CardGrid ref={cardGridRef} $reordering={reordering}>
                      {displayFloorPlans.map((fp, index) => (
                        <div key={fp.id} data-card-index={index}>
                          <FloorPlanCard
                            floorPlan={fp}
                            deletable={!undeletableFloorPlanIds.has(fp.id)}
                            reordering={reordering}
                            index={index}
                            dropIndicator={dropIndicator}
                            isSaving={updateFloorPlanMut.isPending}
                            onSave={handleFloorPlanSave}
                            onDelete={handleFloorPlanDelete}
                          />
                        </div>
                      ))}
                      {addingFloorPlan && !reordering && (
                        <NewFloorPlanCard
                          order={floorPlans.length + 1}
                          isSaving={createFloorPlanMut.isPending}
                          onSave={handleNewFloorPlanSave}
                          onCancel={() => setAddingFloorPlan(false)}
                        />
                      )}
                    </CardGrid>
                  );
                }
                return <EmptyMessage>{t("empty.floorPlan")}</EmptyMessage>;
              })()}
            </>
          )}
          {activeTab === "camera" && (
            <>
              {cameras.length > 0 || addingCamera ? (
                <CardGrid>
                  {sortedCameras.map((cam) => (
                    <CameraCard
                      key={cam.id}
                      camera={cam}
                      displayName={cam.analysisNumber ?? nonAnalysisNumberMap.get(cam.id) ?? cam.defaultCamId}
                      selecting={selectingCameras}
                      selected={selectedCameraIds.has(cam.id)}
                      isSaving={updateCameraImageMut.isPending && updateCameraImageMut.variables?.cameraOrientationId === cam.id}
                      isVersionInactive={isVersionInactive}
                      onToggleSelect={handleToggleCameraSelect}
                      onImageSave={handleCameraImageSave}
                      onDelete={handleCameraDelete}
                    />
                  ))}
                  {addingCamera && !selectingCameras && (
                    <NewCameraCard
                      isSaving={createCameraMut.isPending}
                      onSave={handleNewCameraSave}
                      onCancel={() => setAddingCamera(false)}
                    />
                  )}
                </CardGrid>
              ) : (
                <EmptyMessage>{t("empty.camera")}</EmptyMessage>
              )}
            </>
          )}
              {activeTab === "whitelist" && (
                <WhitelistTab clientUid={clientUid} />
              )}
            </TabContentInner>
          </ScrollArea>
        </TabContent>
      </TabSection>
      <DeleteConfirmDialog
        open={deleteTarget !== null}
        title={
          deleteTarget?.type === "camera"
            ? t("dialog.deleteCamera")
            : t("dialog.deleteFloorPlan")
        }
        name={deleteTarget?.name ?? ""}
        messageKey={
          deleteTarget?.type === "camera"
            ? "dialog.deleteCameraMessage"
            : "dialog.deleteMessage"
        }
        isDeleting={deleteCameraMut.isPending || deleteFloorPlanMut.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
      <DeleteConfirmDialog
        open={bulkDeleteOpen}
        title={t("dialog.deleteCamera")}
        name=""
        messageKey=""
        message={t("dialog.bulkDeleteCameraMessage", { count: selectedCameraIds.size })}
        isDeleting={deleteCameraMut.isPending}
        onConfirm={handleBulkDeleteConfirm}
        onCancel={() => setBulkDeleteOpen(false)}
      />
      <MultiCameraModal
        open={multiCameraOpen}
        onClose={() => setMultiCameraOpen(false)}
        onComplete={handleMultiCameraComplete}
      />
    </Container>
  );
}

// --- Styles ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px 0;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const TabSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 12px 24px 0;
`;

const TabRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  flex-shrink: 0;
  margin-bottom: 8px;
`;

const TabList = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const TabTrigger = styled.button<{ $active?: boolean }>`
  background: unset;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  height: 48px;
  position: relative;
  padding: 0 2px;
  color: ${token("colors.text.primary")};
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  &::before {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 2px;
    background-color: ${({ $active }) => ($active ? "currentColor" : "transparent")};
  }
`;

// 스크롤은 DS ScrollArea 가 담당 (raw overflow-y:auto 는 OS 기본 스크롤바라 다른 화면과 모양이 달랐음)
const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TabContentInner = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardGrid = styled.div<{ $reordering?: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  padding-bottom: 24px;
  padding-left: ${({ $reordering }) => ($reordering ? "8px" : "0")};
  padding-right: ${({ $reordering }) => ($reordering ? "8px" : "0")};
`;

const EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 12px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  color: ${token("colors.text.secondary")};
`;

const CameraMenuContent = styled(RadixPopover.Content)`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  min-width: 200px;
  border-radius: 16px;
  border: 1px solid #E6E8F0;
  background: #fff;
  box-shadow: -20px 20px 40px -4px rgba(105, 111, 140, 0.24),
    0px 0px 2px 0px rgba(105, 111, 140, 0.16);
  outline: none;
  z-index: 50;
`;

const CameraMenuItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 7px 12px;
  border: none;
  background: transparent;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  cursor: pointer;

  &:hover {
    background: ${token("colors.action.hover")};
  }
`;
